<template>
  <transition name="modal-fade">
    <div class="modal-backdrop-5">
      <div class="modal-5" role="dialog">
        <header class="modal-header">
          <h4 class="modal-title">Purchase Request Details</h4>
          <button type="button" class="btn-close" @click="$emit('close')" aria-label="Close modal"></button>
        </header>
        <section class="modal-body">
          <div v-if="request">
            <div class="section">
              <div class="info-row">
                <div class="info-col">
                  <table class="info-table">
                    <tbody>
                      <tr>
                        <td class="info-label">Request No</td>
                        <td class="info-value">{{ request.purchaseRequestNo }}</td>
                      </tr>
                      <tr>
                        <td class="info-label">Date</td>
                        <td class="info-value">{{ formatDate(request.createdAt) }}</td>
                      </tr>
                      <tr>
                        <td class="info-label">Status</td>
                        <td class="info-value"><span :class="statusClass(request.status)">{{ request.status }}</span></td>
                      </tr>
                      <tr>
                        <td class="info-label">Urgency</td>
                        <td class="info-value"><span :class="urgencyClass(request.urgency)">{{ request.urgency }}</span></td>
                      </tr>
                    </tbody>
                  </table>
                </div>
                <div class="info-col">
                  <table class="info-table">
                    <tbody>
                      <tr>
                        <td class="info-label">Department</td>
                        <td class="info-value">{{ request.Department?.name || request.department || request.departmentName || '-' }}</td>
                      </tr>
                      <tr>
                        <td class="info-label">Project</td>
                        <td class="info-value">{{ request.Project?.name || request.project || request.projectName || '-' }}</td>
                      </tr>
                      <tr>
                        <td class="info-label">Requester</td>
                        <td class="info-value">{{ request.requestedBy?.name || '-' }}</td>
                      </tr>
                      <tr>
                        <td class="info-label">Justification</td>
                        <td class="info-value">{{ request.justification || '-' }}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
              <table v-if="request.description && String(request.description).trim()" class="info-table" style="margin-top: 4px;">
                <tbody>
                  <tr>
                    <td class="info-label" style="width: 14%;">Description</td>
                    <td class="info-value">{{ request.description }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <div v-if="hasAttachments" class="section attachments-section">
              <h5>ATTACHMENTS <span class="attachments-badge">({{ attachmentsCount }})</span></h5>
              <div class="attachments-list">
                <div
                  v-for="att in request.attachments"
                  :key="att.id"
                  class="attachment-item"
                >
                  <span class="attachment-name">{{ att.originalName || att.filePath || 'Attachment' }}</span>
                  <span class="attachment-actions">
                    <button type="button" class="btn-preview" @click="openPreview(att)" title="Preview">Preview</button>
                    <a :href="attachmentUrl(att)" target="_blank" rel="noopener noreferrer" class="btn-download" title="Download">Download</a>
                  </span>
                </div>
              </div>
            </div>
            <!-- Preview overlay -->
            <div v-if="previewAttachment" class="preview-overlay" @click.self="closePreview">
              <div class="preview-box">
                <div class="preview-header">
                  <span class="preview-title">{{ previewAttachment?.originalName || 'Preview' }}</span>
                  <button type="button" class="btn-close-preview" @click="closePreview" aria-label="Close">&times;</button>
                </div>
                <div class="preview-body">
                  <img v-if="isPreviewableImage(previewAttachment)" :src="attachmentUrl(previewAttachment)" class="preview-content" alt="Preview" />
                  <iframe v-else-if="isPreviewablePdf(previewAttachment)" :src="attachmentUrl(previewAttachment)" class="preview-content" title="PDF Preview"></iframe>
                  <div v-else class="preview-fallback">
                    <p>Preview not available for this file type.</p>
                    <a :href="attachmentUrl(previewAttachment)" target="_blank" rel="noopener noreferrer" class="btn-download">Download</a>
                  </div>
                </div>
              </div>
            </div>
            <div class="section items-section">
              <h5>ITEMS</h5>
              <div class="table-scroll">
                <table class="table">
                  <thead>
                    <tr>
                      <th>No.</th>
                      <th>Item</th>
                      <th>Req Description</th>
                      <th v-if="hasVehicleItems">Vehicle</th>
                      <th>Unit</th>
                      <th>Qty</th>
                      <th>Est. Unit Price <span class="price-source-hint" title="From product purchase price or service unit price when not overridden">ⓘ</span></th>
                      <th>Est. Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="(item, idx) in request.items" :key="item.id">
                      <td>{{ idx + 1 }}</td>
                      <td>{{ item.product?.name || item.service?.name || '—' }}</td>
                      <td>{{ item.description || '—' }}</td>
                      <td v-if="hasVehicleItems">{{ formatVehicles(item.vehicles) }}</td>
                      <td>{{ item.product?.unit?.unit || item.service?.unit?.name || '—' }}</td>
                      <td>{{ item.quantity }}</td>
                      <td>{{ formatMoney(item.estimatedUnitPrice) }}</td>
                      <td>{{ formatMoney(item.estimatedTotalPrice) }}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <div class="total-row">
                <h6 class="fw-bold">Total Amount:</h6> {{ formatMoney(computedTotalAmount) }}
              </div>
            </div>
            <div v-if="request.approvals && request.approvals.length" class="section">
              <h5>Approval Timeline</h5>
              <div v-for="approval in request.approvals" :key="approval.id" class="approval-item" :class="approval.status">
                <div class="approval-header">
                  <span>{{ approval.status }} by {{ approval.approver?.name }} on {{ formatDate(approval.updatedAt) }}</span>
                  <span class="approval-decision" :class="approval.status">{{ approval.status }}</span>
                </div>
                <div v-if="approval.comment" class="approval-comments">Comment: {{ approval.comment }}</div>
              </div>
            </div>
          </div>
          <div v-else class="text-center text-danger">No details found.</div>
        </section>
        <footer class="modal-footer">
          <button class="btn btn-secondary" @click="$emit('close')">Close</button>
        </footer>
      </div>
    </div>
  </transition>
</template>

<script>
export default {
  name: 'PurchaseRequestDetailsModal',
  props: {
    request: { type: Object, required: true }
  },
  data() {
    return { previewAttachment: null };
  },
  computed: {
    computedTotalAmount() {
      const req = this.request;
      if (!req) return 0;
      const fromRequest = Number(req.totalAmount);
      if (fromRequest > 0) return fromRequest;
      const items = req.items || [];
      const sum = items.reduce((acc, it) => {
        const total = Number(it.estimatedTotalPrice);
        if (!isNaN(total)) return acc + total;
        const qty = Number(it.quantity) || 0;
        const unit = Number(it.estimatedUnitPrice) || 0;
        return acc + qty * unit;
      }, 0);
      return sum;
    },
    attachmentsCount() {
      const atts = this.request?.attachments;
      return Array.isArray(atts) ? atts.length : 0;
    },
    hasDetails() {
      const j = this.request?.justification;
      const d = this.request?.description;
      return !!(j && String(j).trim()) || !!(d && String(d).trim());
    },
    hasAttachments() {
      return this.attachmentsCount > 0;
    },
    hasVehicleItems() {
      const items = this.request?.items || [];
      return items.some(item => Array.isArray(item.vehicles) && item.vehicles.length > 0);
    }
  },
  methods: {
    attachmentUrl(att) {
      if (!att?.filePath) return '#';
      const base = process.env.VUE_APP_URL || '';
      return `${base.replace(/\/$/, '')}/files/${att.filePath}`;
    },
    isPreviewableImage(att) {
      const name = (att?.originalName || att?.filePath || '').toLowerCase();
      return /\.(jpg|jpeg|png|gif|webp|bmp|svg)$/i.test(name);
    },
    isPreviewablePdf(att) {
      const name = (att?.originalName || att?.filePath || '').toLowerCase();
      return /\.pdf$/i.test(name);
    },
    openPreview(att) {
      this.previewAttachment = att;
    },
    closePreview() {
      this.previewAttachment = null;
    },
    formatDate(date, withTime = false) {
      if (!date) return '';
      const d = new Date(date);
      return withTime
        ? d.toLocaleString()
        : d.toLocaleDateString();
    },
    formatMoney(val) {
      if (val == null) return '';
      try {
        return new Intl.NumberFormat('en-US', { 
          style: 'currency', 
          currency: 'KES', 
          currencyDisplay: 'code' 
        }).format(val);
      } catch (e) {
        return val;
      }
    },
    formatVehicles(vehicles) {
      if (!Array.isArray(vehicles) || vehicles.length === 0) return '\u2014';
      return vehicles.map(v => {
        const label = [v.plateNumber, v.make, v.model].filter(Boolean).join(' ');
        return label || '\u2014';
      }).join(', ');
    },
    statusClass(status) {
      const s = String(status || '').toLowerCase().trim();
      if (s.includes('completed') || s.includes('approved') || s === 'active') return 'text-success';
      if (s.includes('pending') || s.includes('draft') || s.includes('waiting')) return 'text-warning';
      if (s.includes('rejected') || s.includes('cancelled') || s.includes('declined')) return 'text-danger';
      return '';
    },
    urgencyClass(urgency) {
      const u = String(urgency || '').toLowerCase().trim();
      if (u === 'high') return 'text-danger';
      if (u === 'medium') return 'text-warning';
      if (u === 'low') return 'text-success';
      return '';
    }
  }
}
</script>

<style scoped>
.modal-backdrop-5 {
  position: fixed;
  top:0; right:0; bottom:0; left:0 ;
  background-color: rgba(0, 0, 0, 0.7) !important;
  z-index: 1000;
  display: flex !important;
  justify-content: center !important;
  align-items: flex-start !important;
  overflow-y: auto;
  overflow-x: hidden;
  padding-top: 3% !important;
  padding-bottom: 3% !important;
}
.modal-5 {
  background: #FFFFFF;
  box-shadow: 2px 2px 20px 1px rgba(0, 0, 0, 0.5);
  display: flex !important;
  flex-direction: column;
  width: 90% !important;
  max-width: 900px;
  height: auto !important;
  max-height: 90vh;
  overflow: hidden;
  padding: 0 !important;
  z-index: 1001;
  border-radius: 8px;
}

.modal-5 > .modal-header,
.modal-5 > .modal-body,
.modal-5 > .modal-footer {
  padding: 8px 12px;
}

.modal-body {
  overflow-y: auto;
}


  .modal-header {
    padding: 11px 15px;
    display: flex;
    gap: 10px;
  }
.modal-title {
  font-size: 15px;
  font-weight: 600;
  margin: 0;
}

  .modal-headerr {
    position: relative;
    border-bottom: 1px solid #eeeeee;
    color: #000;
    justify-content: space-between;
    padding: 10px 10px 10px 20px;
  }

  .modal-footer {
    border-top: 1px solid #eeeeee;
  }

  .modal-body {
    position: relative;
    padding: 6px 10px;
  }

  .btn-close {
    position: absolute;
    top: 0;
    right: 0;
    border: none;
    font-size: 13px;
    padding: 10px;
    cursor: pointer;
    font-weight: bold;
    color: #000000;
    background: transparent;
  }

  .btn-green {
    color: white;
    background: #000000;
    border: 1px solid #000000;
    border-radius: 2px;
  }

  .modal-fade-enter,
  .modal-fade-leave-to {
    opacity: 0;
  }

  .modal-fade-enter-active,
  .modal-fade-leave-active {
    transition: opacity .5s ease;
  }

.section {
  margin-bottom: 2px;
}
.section h5 {
  font-size: 12px;
  font-weight: 600;
  margin-bottom: 1px;
  color: #333;
}
.section.attachments-section {
  margin-top: 16px;
}
.section.items-section {
  margin-top: 16px;
}
.text-small {
  font-size: 11px;
}
.info-row {
  display: flex;
  gap: 12px;
  margin-bottom: 2px;
}
@media (max-width: 600px) {
  .info-row {
    flex-direction: column;
  }
}
.info-col {
  flex: 1;
  min-width: 0;
}
.attachments-badge {
  font-weight: normal;
  font-size: 0.85em;
  color: #666;
}
.attachments-list {
  display: flex;
  flex-direction: column;
  gap: 2px;
}
.attachment-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 4px 6px;
  background: #f8f9fa;
  border: 1px solid #eee;
  border-radius: 4px;
  font-size: 11px;
}
.attachment-name {
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.attachment-actions {
  display: flex;
  gap: 8px;
  flex-shrink: 0;
}
.btn-preview, .btn-download {
  font-size: 10px;
  padding: 1px 12px;
  border-radius: 9999px;
  text-decoration: none;
  cursor: pointer;
  display: inline-block;
  line-height: 1.1;
}
.btn-preview {
  background: #ea580c;
  color: white;
  border: none;
}
.btn-preview:hover {
  background: #c2410c;
}
.btn-download {
  background: transparent;
  color: #ea580c;
  border: 1px solid #ea580c;
}
.btn-download:hover {
  background: #fff7ed;
}
.preview-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.6);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1100;
  padding: 20px;
}
.preview-box {
  background: #fff;
  border-radius: 8px;
  width: 45vw;
  max-width: 45vw;
  max-height: 85vh;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3);
}
.preview-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 12px;
  background: #f8f9fa;
  border-bottom: 1px solid #eee;
  font-size: 11px;
}
.preview-title {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.btn-close-preview {
  background: none;
  border: none;
  font-size: 18px;
  cursor: pointer;
  padding: 0 4px;
  line-height: 1;
  color: #666;
}
.btn-close-preview:hover {
  color: #000;
}
.preview-body {
  flex: 1;
  overflow: auto;
  padding: 11px;
  min-height: 200px;
}
.preview-content {
  max-width: 100%;
  max-height: 70vh;
  display: block;
}
.preview-body iframe.preview-content {
  width: 100%;
  height: 70vh;
  border: none;
}
.preview-fallback {
  padding: 24px;
  text-align: center;
  font-size: 13px;
  color: #666;
}
.preview-fallback .btn-download {
  font-size: 13px;
  padding: 11px 20px;
  margin-top: 11px;
}
.info-table {
  width: 100%;
  border-collapse: collapse;
  margin-bottom: 1px;
}
.info-table .info-group-header td {
  padding: 1px 2px 1px;
  font-size: 10px;
  font-weight: 600;
  text-transform: uppercase;
  color: #555;
  background: #f8f9fa;
  border-bottom: 1px solid #eee;
  border-top: 1px solid #eee;
}
.info-table .info-group-header:first-child td {
  border-top: none;
}
.info-table .info-label {
  width: 28%;
  padding: 1px 8px;
  color: #555;
  font-size: 11px;
  background: #e9ecef !important;
  border-bottom: 1px solid #dee2e6;
  vertical-align: top;
}
.info-table .info-value {
  padding: 1px 8px;
  border-bottom: 1px solid #f0f0f0;
  font-weight: normal;
  font-size: 11px;
  color: #222;
}
.info-table tr:last-child .info-label,
.info-table tr:last-child .info-value {
  border-bottom: none;
}
.table-scroll {
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
}
.table-scroll::-webkit-scrollbar {
  height: 6px;
}
.table-scroll::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 3px;
}
.table-scroll::-webkit-scrollbar-thumb {
  background: #ccc;
  border-radius: 3px;
}
.table {
  width: 100%;
  border-collapse: collapse;
  margin-bottom: 4px;
  min-width: 700px;
}
.table th, .table td {
  border: 1px solid #eee;
  padding: 2px 8px;
  font-size: 11px;
  text-align: left;
  white-space: nowrap;
}
.table th {
  background: #f8f9fa;
  font-size: 11px;
}
.price-source-hint {
  cursor: help;
  opacity: 0.6;
  font-size: 0.9em;
}
.total-row {
  text-align: right;
  font-weight: bold;
  margin-top: 2px;
  font-size: 11px;
}
.approval-item {
  background: #f8f9fa;
  border-left: 4px solid #ddd;
  margin-bottom: 4px;
  padding: 5px 8px;
  border-radius: 4px;
  font-size: 11px;
}
.approval-item.APPROVED {
  border-left-color: #28a745;
  background: #f1f9f3;
}
.approval-item.REJECTED {
  border-left-color: #dc3545;
  background: #fdf2f2;
}
.approval-header {
  display: flex;
  align-items: center;
  margin-bottom: 2px;
}
.approval-decision {
  font-size: 10px;
  padding: 2px 6px;
  border-radius: 10px;
  text-transform: uppercase;
  font-weight: bold;
  margin-left: auto;
}
.approval-decision.APPROVED {
  background: #28a745;
  color: white;
}
.approval-decision.REJECTED {
  background: #dc3545;
  color: white;
}
.approval-comments {
  background: white;
  padding: 4px 6px;
  border-radius: 4px;
  margin-top: 3px;
  font-size: 11px;
  border: 1px solid #e9ecef;
}
.btn-close {
  background: none;
  border: none;
  font-size: 18px;
  cursor: pointer;
}
.text-success {
  color: #28a745;
  font-weight: 600;
}
.text-warning {
  color: #f0ad4e;
  font-weight: 600;
}
.text-danger {
  color: #dc3545;
  font-weight: 600;
}
</style>
